import React, { Suspense, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, useGLTF } from '@react-three/drei';
import styled from 'styled-components';

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0a192f;
`;

const Canvas3D = styled(Canvas)`
  width: 100% !important;
  height: 100% !important;
  display: block !important;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffd700;
  font-size: 1.2rem;
  z-index: 100;
  flex-direction: column;
  gap: 1.5rem;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 215, 0, 0.3);
  border-top-color: #ffd700;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff6b6b;
  font-size: 1rem;
  z-index: 100;
  flex-direction: column;
  gap: 2rem;
  text-align: center;
  padding: 3rem;
  
  h3 {
    color: #ffd700;
    margin: 0;
    font-size: 1.3rem;
  }

  p {
    margin: 0.5rem 0;
    color: #aaa;
    font-size: 0.95rem;
    font-family: monospace;
  }

  code {
    background: rgba(255, 215, 0, 0.1);
    padding: 0.5rem 1rem;
    border-radius: 6px;
    color: #ffd700;
    display: block;
    margin: 1rem 0;
    border: 1px solid rgba(255, 215, 0, 0.2);
  }
`;

const Info = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.85);
  color: #ffd700;
  padding: 1rem 1.5rem;
  border-radius: 20px;
  font-weight: 600;
  text-align: center;
  z-index: 10;
  max-width: 90%;
  font-size: 0.9rem;
  border: 1px solid rgba(255, 215, 0, 0.3);
`;

// Component to load and display the GLB model
// Hook must be called unconditionally at top level
function Model({ modelPath, onError }) {
  console.log('🔍 Loading GLB from:', modelPath);
  
  // useGLTF hook called unconditionally
  const gltf = useGLTF(modelPath, true);
  
  if (!gltf || !gltf.scene) {
    console.error('❌ GLB load failed - no scene');
    onError(new Error('Failed to load GLB scene'));
    return null;
  }
  
  console.log('✅ GLB loaded successfully');
  return <primitive object={gltf.scene} />;
}

export default function Model3DViewer({ modelPath }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleError = (err) => {
    console.error('Model3DViewer Error:', err);
    setError(err);
    setLoading(false);
  };

  return (
    <Container>
      {error && (
        <ErrorMessage>
          <h3>⚠️ Failed to Load 3D Model</h3>
          <div>
            <p>Error: {error.message}</p>
            <p>Path attempted:</p>
            <code>{modelPath}</code>
            <p style={{ color: '#aaa', fontSize: '0.85rem' }}>
              Make sure the GLB file exists in:
              <br />
              <code style={{ display: 'inline' }}>public/assets/models/Apartment1Bed.glb</code>
              <br />
              Then restart your dev server.
            </p>
          </div>
        </ErrorMessage>
      )}

      {loading && !error && (
        <LoadingOverlay>
          <Spinner />
          <span>Loading 3D Model...</span>
          <p style={{ fontSize: '0.85rem', color: '#aaa', margin: 0 }}>
            Path: {modelPath}
          </p>
        </LoadingOverlay>
      )}

      {!error && (
        <>
          <Canvas3D
            camera={{ position: [5, 3, 5], fov: 50 }}
            gl={{
              antialias: true,
              pixelRatio: Math.min(window.devicePixelRatio, 2),
              alpha: true,
            }}
            onCreated={() => setLoading(false)}
          >
            <Suspense fallback={null}>
              {/* Lighting */}
              <ambientLight intensity={0.7} />
              <directionalLight position={[10, 15, 10]} intensity={1.2} />
              <directionalLight position={[-10, 8, -10]} intensity={0.5} />
              <hemisphereLight intensity={0.5} />

              {/* Camera */}
              <PerspectiveCamera makeDefault position={[5, 3, 5]} fov={50} />

              {/* Load actual GLB model with error handling */}
              <Model modelPath={modelPath} onError={handleError} />

              {/* Controls */}
              <OrbitControls
                enableZoom={true}
                enablePan={true}
                autoRotate={false}
                dampingFactor={0.05}
                rotateSpeed={1}
              />
            </Suspense>
          </Canvas3D>

          <Info>
            🖱️ Drag to rotate • Scroll to zoom • Right-click to pan
          </Info>
        </>
      )}
    </Container>
  );
}
